import { createContext } from "react";

import type { AuthWrapperCustomEvent } from "@jb-manage-trips/types/web-components";

export type CustomerDetails = {
  contact: {
    addresses: {
      addressType: string;
      city: string;
      country: string;
      postcode: string;
      state: string;
      street1: string;
      street2: string;
    }[];
    emails: string[];
    phones: {
      areaCode: string;
      countryCode: string;
      extension: string;
      number: string;
      type: string;
    }[];
  };
  creditCards: {
    cardCode: string;
    expirationDate: string;
    holderName: string;
    number: string;
    type: string;
  }[];
  dateOfBirth: string;
  firstName?: string | undefined;
  gender: string;
  loyaltyDetails: {
    accountBalance?: number;
    airline?: string;
    benefitsDetails?: string[];
    cobrandCardCodes?: string[];
    membershipID: string;
    membershipTier:
      | "MEMBER"
      | "MOSAIC_1"
      | "MOSAIC_2"
      | "MOSAIC_3"
      | "MOSAIC_4"
      | "MOSAIC_5";
    tiles?: number;
  };
  lastName?: string | undefined;
  prefix?: string | undefined;
  profileId: string;
  profileImage?: string | undefined;
  profileImageType?: string | undefined;
  travelBankDetails?:
    | {
        accountNumber: string;
        balance?:
          | {
              amount: string;
              currency: string;
            }
          | undefined;
      }
    | undefined;
};
export type AuthState = {
  IS_B6AUTH_STORED?: boolean;
  IS_CREDIT_PROFILE_USER?: boolean;
  IS_TOKEN_STORED?:
    | {
        stored: boolean;
        tokenType: string;
      }
    | undefined;
  PROFILE?: { customerDetails: CustomerDetails } | undefined;
  ACCESS_TOKEN?: string;
  ID_TOKEN?: string;
};
export const DEFAULT_AUTH_CONTEXT: AuthState = {};
export const AuthContext = createContext(DEFAULT_AUTH_CONTEXT);
export const authReducer = (
  state: AuthState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- The event detail type is generic and depends on the event, so 'any' is required here.
  detail: AuthWrapperCustomEvent<any>["detail"],
): AuthState => {
  switch (detail.action) {
    case "IS_B6AUTH_STORED":
    case "IS_CREDIT_PROFILE_USER":
    case "PROFILE": {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- The event detail value type is dynamic and depends on the action, so 'any' is required here.
      return { ...state, [detail.action]: detail.value };
    }

    case "IS_TOKEN_STORED": {
      return {
        ...state,
        IS_TOKEN_STORED: {
          stored: detail.value.stored,
          tokenType: detail.value.tokenType,
        },
      };
    }

    case "TOKEN": {
      if (detail.value.tokenType === "accessToken") {
        return { ...state, ACCESS_TOKEN: detail.value.token.accessToken };
      }

      if (detail.value.tokenType === "idToken") {
        return {
          ...state,
          ID_TOKEN: detail.value.token.idToken ?? state.ID_TOKEN,
        };
      }

      return state;
    }

    default: {
      return state;
    }
  }
};
